import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {AuthService} from '../../../../shared/services/auth.service';
import {takeUntil} from 'rxjs/operators';
import {Invoice} from '../../../../shared/interface/invoice';
import {InvoiceService} from '../../../../shared/services/invoice.service';
import {ConfirmDialogComponent} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Router} from '@angular/router';
import {ChangeStatusDialogComponent} from '../change-status-dialog/change-status-dialog.component';
import {
  INVOICE_STATUS_PAID, INVOICE_STATUS_REJECTED, INVOICE_STATUS_SENT_FOR_PAYMENT,
  INVOICE_STATUS_SUSPENDED, INVOICE_STATUS_TO_BE_CORRECTED,
  INVOICE_STATUS_TO_BE_REALIZED
} from '../../../../shared/services/dict.service';
import {CreateDocumentDialogComponent} from '../create-document-dialog/create-document-dialog.component';
import {InvoiceDocument} from '../../../../shared/interface/invoice-document';
import {RiskGroup} from "../../../../shared/interface/risk-group";
import {CommissionSettlement} from "../../../../shared/interface/commission-settlement";
import {InvoiceSettlement} from "../../../../shared/interface/invoice-settlement";
import moment from 'moment';
import {InvoiceDocumentService} from "../../../../shared/services/invoice-document.service";
@Component({
  selector: 'app-invoice-details-view',
  templateUrl: './invoice-details-view.component.html',
  styleUrls: ['./invoice-details-view.component.scss']
})
export class InvoiceDetailsViewComponent implements OnInit, OnDestroy {
  @Input() invoice: Observable<Invoice>;
  @Output() changed: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() modifyClicked = new EventEmitter<any>();

  readonly INVOICE_STATUS_TO_BE_REALIZED = INVOICE_STATUS_TO_BE_REALIZED;
  readonly INVOICE_STATUS_PAID = INVOICE_STATUS_PAID;
  readonly INVOICE_STATUS_SUSPENDED = INVOICE_STATUS_SUSPENDED;
  readonly INVOICE_STATUS_REJECTED = INVOICE_STATUS_REJECTED;
  readonly INVOICE_STATUS_TO_BE_CORRECTED = INVOICE_STATUS_TO_BE_CORRECTED;
  readonly INVOICE_STATUS_SENT_FOR_PAYMENT = INVOICE_STATUS_SENT_FOR_PAYMENT;

  invoiceObject: Invoice;
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'billingMonth';
  serviceDate = null;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private auth: AuthService,
    private invoiceService: InvoiceService,
    private invoiceDocumentService: InvoiceDocumentService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.invoice
      .pipe(takeUntil(this.destroyed$))
      .subscribe((invoice: Invoice) => {
        this.invoiceObject = invoice;
        this.serviceDate = this.invoiceObject.serviceDate;
        this.invoiceObject?.settlements.sort((a: InvoiceSettlement, b: InvoiceSettlement) =>
        new Date(a.contractSettlement.commissionSettlement.billingMonth).getTime() - new Date(b.contractSettlement.commissionSettlement.billingMonth).getTime());
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  downloadPdf(): void {
    this.invoiceService.downloadPdf(this.invoiceObject.id);
  }

  delete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć fakturę?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.invoiceService.delete(this.invoiceObject.id)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto fakturę', '', {duration: 2000});
              this.router.navigate(['/faktury']);
            },
            error: (error: HttpErrorResponse) => {
              this.snack.open('Nie można usunąć faktury', error.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }

  changeStatus(
    actionCode: string,
    enableNote: boolean,
    noteLabel: string,
    newStatusName: string
  ): void {
    const dialogRef = this.dialog.open(ChangeStatusDialogComponent, {
      width: '500px',
      data: {
        invoice: this.invoiceObject,
        enableNote,
        noteLabel,
        actionCode,
        newStatusName,
        sortBy: this.invoiceObject,
        tagLine: 'Po wykonaniu akcji nie ma możliwości jej cofnięcia.'
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.changed.emit();
    });
  }

  addDocument(): void {
    const dialogRef = this.dialog.open(CreateDocumentDialogComponent, {
      width: '500px',
      data: {
        invoice: this.invoiceObject,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.changed.emit();
    });
  }

  downloadDocument(document: InvoiceDocument): void {
    this.invoiceService.downloadDocument(
      this.invoiceObject.uuid,
      document.uuid
    );
  }


  deleteDocument(document: InvoiceDocument): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć ' + document.type.name + '?'  },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.invoiceService.deleteDocument(document.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto', '', {duration: 2000});
              this.changed.emit();
            },
            error: (error: HttpErrorResponse) => {
              this.snack.open('Nie można usunąć', error.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }

  modify(): void {
    this.modifyClicked.emit();
  }


  downloadNoteDocument(documentId: string): void {
    this.invoiceDocumentService.downloadPdf(
      documentId
    );
  }

  deleteNoteDocument(documentId: string): void {
    if (!documentId) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: { text: 'Czy na pewno chcesz usunąć notę?' },
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.invoiceDocumentService.delete(documentId)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto notę', '', {duration: 2000});
              this.router.navigate(['/faktury/szczegoly/', this.invoiceObject.id]);
            },
            error: (error: HttpErrorResponse) => {
              this.snack.open('Nie można usunąć noty', error.status.toString(), {duration: 2000});
            }
          });
      });
  }
}
